import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notesApi } from "../../api/workspace-notes.api";
import { boardsApi } from "../../api/workspace-boards.api";
import type { WorkspaceReference } from "../../api/references.api";
import { useAuthStore } from "../../store/auth.store";
import { noteReferenceToken } from "./NoteComposer";
export function AttachReference({
  reference,
  kind,
  onClose,
}: {
  reference: WorkspaceReference;
  kind: "note" | "board";
  onClose: () => void;
}) {
  const school = useAuthStore((s) => s.activeSchoolId),
    user = useAuthStore((s) => s.user?.id);
  const [page, setPage] = useState(1),
    [target, setTarget] = useState(""),
    [requestId] = useState(() => crypto.randomUUID());
  const client = useQueryClient();
  const list = useQuery({
    queryKey: ["reference-targets", school, user, kind, page],
    queryFn: async () =>
      kind === "note" ? notesApi.list(page) : boardsApi.list(page),
    gcTime: 0,
  });
  const save = useMutation({
    mutationFn: async () => {
      if (kind === "note") {
        const note = await notesApi.get(target);
        if (!note.canEdit)
          throw Error("Vous ne pouvez pas modifier cette note.");
        const refs = note.references ?? [];
        const has = refs.some(
          (r) =>
            r.entityType === reference.entityType &&
            r.entityId === reference.entityId,
        );
        if (!has && refs.length >= 20)
          throw Error("Cette note contient déjà 20 objets.");
        const token = noteReferenceToken(reference);
        const body = note.body.includes(token)
          ? note.body
          : note.body + "\n" + token;
        if (body.length > 20000) throw Error("Cette note est trop longue.");
        if (has && body === note.body) return;
        await notesApi.update(target, {
          title: note.title,
          body,
          visibility: note.visibility,
          memberIds: note.memberIds,
          references: has ? refs : [...refs, reference],
          mentionIds: note.mentionIds ?? [],
          fileIds: note.fileIds ?? [],
          version: note.version,
          requestId,
        });
      } else {
        const board = await boardsApi.get(target);
        if (!board.canEdit)
          throw Error("Vous ne pouvez pas modifier ce tableau.");
        const cards = board.scene.cards ?? [];
        if (
          cards.some(
            (c) =>
              c.reference.entityType === reference.entityType &&
              c.reference.entityId === reference.entityId,
          )
        )
          return;
        if (cards.length >= 50)
          throw Error("Ce tableau contient déjà 50 cartes.");
        await boardsApi.save(target, {
          title: board.title,
          visibility: board.visibility,
          memberIds: board.memberIds,
          scene: {
            ...board.scene,
            schemaVersion: board.scene.attachments ? 3 : 2,
            cards: [
              ...cards,
              {
                id: requestId,
                reference,
                x: 80 + (cards.length % 3) * 270,
                y: 140 + Math.floor(cards.length / 3) * 190,
              },
            ],
          },
          version: board.version,
          requestId,
        });
      }
    },
    onSuccess: async () => {
      await client.invalidateQueries({
        predicate: (q) =>
          q.queryKey.some((k) => typeof k === "string" && /note|board/.test(k)),
      });
    },
  });
  return (
    <section aria-label="Choisir la destination" className="reference-attach">
      <p>
        Ajouter l’objet à {kind === "note" ? "une note" : "un tableau"}{" "}
        existant. Le contenu source reste soumis à ses droits.
      </p>
      {list.isError ? (
        <p role="alert">Liste indisponible.</p>
      ) : (
        <label>
          Destination
          <select
            aria-label="Destination"
            value={target}
            disabled={save.isPending}
            onChange={(e) => {
              setTarget(e.target.value);
              save.reset();
            }}
          >
            <option value="">Choisir…</option>
            {list.data?.items
              .filter(
                (item) =>
                  kind === "board" || ("canEdit" in item && item.canEdit),
              )
              .map((item) => (
                <option key={item.id} value={item.id}>
                  {item.title}
                </option>
              ))}
          </select>
        </label>
      )}
      <button
        type="button"
        disabled={page === 1 || save.isPending}
        onClick={() => {
          setPage(page - 1);
          setTarget("");
        }}
      >
        Précédent
      </button>
      <button
        type="button"
        disabled={!list.data?.hasMore || save.isPending}
        onClick={() => {
          setPage(page + 1);
          setTarget("");
        }}
      >
        Suivant
      </button>
      {save.isError && (
        <p role="alert">
          {save.error instanceof Error
            ? save.error.message
            : "Ajout impossible."}{" "}
          Aucun remplacement forcé : en cas de conflit, rouvrez la destination.
        </p>
      )}
      {save.isSuccess ? (
        <p role="status">Objet ajouté.</p>
      ) : (
        <button
          type="button"
          disabled={!target || save.isPending}
          onClick={() => save.mutate()}
        >
          Confirmer l’ajout
        </button>
      )}
      <button type="button" disabled={save.isPending} onClick={onClose}>
        Fermer
      </button>
    </section>
  );
}
